import { LitElement, html, css } from 'lit';

const MAX_WORDS = 100;

function countWords(text) {
  const t = (text || '').trim();
  return t ? t.split(/\s+/).length : 0;
}

/**
 * Reusable WYSIWYG (contenteditable) instructions editor, capped at 100 words.
 * Set `.value` with the initial HTML; the editor emits a `change` event with
 * `{ html, words, over }` on every edit. Edits that push the word count over the
 * limit are undone, but shortening is always allowed.
 *
 * The word counter sits in a footer inside the card; whatever is put in `slot="footer"`
 * (status badge, buttons) goes next to it, on the right.
 */
export class InstructionsEditor extends LitElement {
  static properties = {
    value: { type: String },
    disabled: { type: Boolean },
    _words: { state: true },
  };

  constructor() {
    super();
    this.value = '';
    this.disabled = false;
    this._words = 0;
    this._ready = false;
    this._lastWords = 0;
    this._seededValue = undefined;
  }

  get _editor() { return this.renderRoot?.querySelector('.editor'); }
  get over() { return this._words > MAX_WORDS; }
  get currentHtml() { return this._editor?.innerHTML ?? this.value ?? ''; }

  updated() {
    // Seed once, and re-seed when the parent assigns a genuinely new value
    // (e.g. after a reset) — not while the user is editing.
    if (this._editor && this._seededValue !== this.value) this._seed();
  }

  _seed() {
    this._editor.innerHTML = this.value || '';
    this._seededValue = this.value;
    this._lastWords = countWords(this._editor.innerText);
    this._words = this._lastWords;
    this._ready = true;
  }

  _emit() {
    this.dispatchEvent(new CustomEvent('change', {
      detail: { html: this.currentHtml, words: this._words, over: this.over },
    }));
  }

  _exec(command, value = null) {
    this._editor?.focus();
    document.execCommand(command, false, value);
    this._words = countWords(this._editor?.innerText);
    this._lastWords = this._words;
    this._emit();
  }

  _onInput(e) {
    if (e?.inputType === 'historyUndo' || e?.inputType === 'historyRedo') { this._emit(); return; }
    const words = countWords(this._editor?.innerText);
    if (words > MAX_WORDS && words > this._lastWords) {
      document.execCommand('undo');
      this._words = countWords(this._editor?.innerText);
      this._emit();
      return;
    }
    this._lastWords = words;
    this._words = words;
    this._emit();
  }

  _tb(label, command, value, title = label) {
    return html`<button class="tb-btn" title=${title} ?disabled=${this.disabled}
      @mousedown=${e => { e.preventDefault(); if (!this.disabled) this._exec(command, value); }}>${label}</button>`;
  }

  render() {
    return html`
      <div class="editor-wrap">
        <div class="toolbar">
          ${this._tb(html`<b>B</b>`, 'bold', null, 'Vet')}
          ${this._tb(html`<i>I</i>`, 'italic', null, 'Cursief')}
          <span class="sep"></span>
          ${this._tb('Kop', 'formatBlock', 'H2')}
          ${this._tb('Subkop', 'formatBlock', 'H3')}
          ${this._tb('Tekst', 'formatBlock', 'P')}
          <span class="sep"></span>
          ${this._tb('• Lijst', 'insertUnorderedList')}
          ${this._tb('1. Lijst', 'insertOrderedList')}
          <span class="grow"></span>
          ${this._tb('Opmaak wissen', 'removeFormat')}
        </div>
        <div class="editor" contenteditable=${this.disabled ? 'false' : 'true'} @input=${this._onInput}></div>
        <div class="footer">
          <span class="counter ${this.over ? 'is-over' : ''}"><b>${this._words}</b> / ${MAX_WORDS} woorden${this.over ? ' — kort de tekst in om op te slaan' : ''}</span>
          <slot name="footer"></slot>
        </div>
      </div>
    `;
  }

  static styles = css`
    :host { display: block; }
    .editor-wrap { border: 1px solid var(--mv-border); border-radius: 12px; overflow: hidden; background: var(--mv-surface); }
    .toolbar {
      display: flex; flex-wrap: wrap; align-items: center; gap: 2px; padding: 6px 8px;
      border-bottom: 1px solid var(--mv-border); background: var(--mv-surface-alt, rgba(0,0,0,0.02));
    }
    .tb-btn {
      border: none; background: transparent; color: var(--mv-text);
      border-radius: 6px; padding: 5px 9px; font-size: 0.78rem; font-weight: 500; cursor: pointer; font-family: inherit;
    }
    .tb-btn:hover { background: var(--mv-border); }
    .tb-btn:disabled { opacity: 0.5; cursor: default; }
    .sep { width: 1px; height: 16px; background: var(--mv-border); margin: 0 6px; }
    .grow { flex: 1; }
    .footer {
      display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 12px 14px;
      border-top: 1px solid var(--mv-border); background: var(--mv-surface-alt, rgba(0,0,0,0.02));
    }
    .footer slot { display: contents; }
    .editor {
      min-height: 220px; max-height: 420px; overflow-y: auto; padding: 16px 18px;
      color: var(--mv-text); font-size: 0.85rem; line-height: 1.55; outline: none;
    }
    .editor > :first-child { margin-top: 0; }
    .editor p { margin: 0.9em 0 0.3em; }
    .editor li { margin: 0.15em 0; }
    .editor:empty::before { content: 'Schrijf hier de instructies…'; color: var(--mv-text-muted); }
    .editor h2 { font-size: 1.2rem; margin: 0.6em 0 0.3em; }
    .editor h3 { font-size: 1.05rem; margin: 0.6em 0 0.3em; }
    .editor ul, .editor ol { padding-left: 1.4em; margin: 0.3em 0 0.6em; }
    .counter { font-size: 0.8rem; color: var(--mv-text-muted); }
    .counter b { color: var(--mv-text); }
    .counter.is-over { color: #B45309; font-weight: 600; }
  `;
}

customElements.define('instructions-editor', InstructionsEditor);
